import { z } from 'zod';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { FormData } from './types';
import {
  calculateAgeFromDateString,
  isDateOnOrBeforeToday,
} from './date-utils';

export const GENDERS = ['male', 'female', 'non_binary', 'prefer_not_to_say', 'other'] as const;
export const SEX_AT_BIRTH_VALUES = ['male', 'female'] as const;

const positiveNumberString = (requiredMessage: string, invalidMessage: string) =>
  z.string()
    .min(1, requiredMessage)
    .refine((value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0;
    }, invalidMessage);

const normalizeDialCode = (value: string) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
};

const normalizePhoneNumber = (phone: string, dialCode: string) => {
  const phoneDigits = String(phone || '').replace(/\D/g, '');
  const dialDigits = normalizeDialCode(dialCode).replace(/\D/g, '');
  if (!phoneDigits || !dialDigits) return '';
  return `+${dialDigits}${phoneDigits}`;
};

// Form validation schemas
export const step1Schema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address").min(1, "Email is required"),
  phone: z.string().min(1, "Phone number is required"),
  dateOfBirth: z.string()
    .min(1, "Date of birth is required")
    .refine((value) => isDateOnOrBeforeToday(value), "Date of birth cannot be in the future"),
  age: z.string().min(1, "Age is required"),
  gender: z.enum(GENDERS, {
    required_error: "Gender is required",
  }),
  sexAtBirth: z.string().optional(),
  location: z.string().min(1, "Country is required"),
  cityOrCounty: z.string().max(200, "City / county must be at most 200 characters").optional(),
  zipCode: z.string().optional(),
  phone_country_code: z.string().min(1, "Phone country code is required"),
  preferredMeasurementSystem: z.enum(['metric', 'imperial'], {
    required_error: "Please select your preferred measurement system",
  }),
}).superRefine((data, ctx) => {
  const normalizedPhone = normalizePhoneNumber(data.phone, data.phone_country_code);
  if (normalizedPhone && !isValidPhoneNumber(normalizedPhone)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Enter a complete phone number",
      path: ["phone"],
    });
  }

  // Sex at birth is required only when gender is not male or female
  if (data.gender !== 'male' && data.gender !== 'female') {
    if (!data.sexAtBirth || (data.sexAtBirth !== 'male' && data.sexAtBirth !== 'female')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Sex at birth is required when gender is not Male or Female",
        path: ["sexAtBirth"],
      });
    }
  }
});

export const step2Schema = z.object({
  weightKg: positiveNumberString("Weight is required", "Enter a valid weight"),
  heightCm: positiveNumberString("Height is required", "Enter a valid height"),
  activityLevel: z.string().optional(),
  feedingType: z.string().optional(),
  formulaId: z.string().min(1, "Formula is required"),
  drvFormulaId: z.string().min(1, "DRV formula is required"),
  waistCircumferenceCm: z.string().optional(),
  hipCircumferenceCm: z.string().optional(),
  bodyFatPercentage: z.string().optional(),
  pregnancyStatus: z.string().optional(),
  pregnancyTrimester: z.string().optional(),
  prePregnancyWeightKg: z.string().optional(),
  lactationStatus: z.string().optional(),
  twinPregnancy: z.string().optional(),
  weightLossGoal: z.string().optional(),
  customCalorieAdjustment: z.string().optional(),
  allergies: z.string().optional(),
  foodIntolerances: z.string().optional(),
  dietaryPreferences: z.string().optional(),
  cuisineTypes: z.string().optional(),
  medicalConditions: z.string().optional(),
  diabetesType: z.string().optional(),
  thyroidType: z.string().optional(),
  healthGoal: z.string().optional(),
  gender: z.string().optional(), // Include gender for validation
  sexAtBirth: z.string().optional(), // Include sexAtBirth for validation
  dateOfBirth: z.string().optional(), // Include dateOfBirth for infant age validation
}).superRefine((data, ctx) => {
  const effectiveGenderForPregnancy = (data.gender === 'male' || data.gender === 'female')
    ? data.gender
    : data.sexAtBirth;
  const isFemale = effectiveGenderForPregnancy === 'female';
  
  // Only validate pregnancy/lactation fields for females
  if (!isFemale) {
    return; // Skip pregnancy/lactation validation for males
  }
  
  // Calculate age to check if female is below 18
  const calculatedAge = calculateAgeFromDateString(data.dateOfBirth);
  const isFemaleUnder18 = calculatedAge === null || calculatedAge < 18;
  
  // Skip pregnancy/lactation validation for females below 18
  if (isFemaleUnder18) {
    return;
  }
  
  const isPregnant = data.pregnancyStatus === 'pregnant' ||
    data.pregnancyStatus === 'first' ||
    data.pregnancyStatus === 'second' ||
    data.pregnancyStatus === 'third';
  const isLactating = Boolean(data.lactationStatus) && data.lactationStatus !== 'none';

  if (isPregnant) {
    if (!data.pregnancyTrimester || data.pregnancyTrimester.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["pregnancyTrimester"],
      });
    }
    if (!data.prePregnancyWeightKg || data.prePregnancyWeightKg.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["prePregnancyWeightKg"],
      });
    } else {
      const prePregnancyWeightKg = Number(data.prePregnancyWeightKg);
      if (!Number.isFinite(prePregnancyWeightKg) || prePregnancyWeightKg <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Enter a valid weight",
          path: ["prePregnancyWeightKg"],
        });
      }
    }
    if (!data.twinPregnancy || data.twinPregnancy.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["twinPregnancy"],
      });
    }
  }

  if (!data.lactationStatus || data.lactationStatus.trim() === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "This field is required",
      path: ["lactationStatus"],
    });
  }

  if (isLactating) {
    if (!isPregnant && (!data.twinPregnancy || data.twinPregnancy.trim() === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["twinPregnancy"],
      });
    }
    if (!data.weightLossGoal || data.weightLossGoal.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["weightLossGoal"],
      });
    }
  }
  
  // Activity level is conditionally required by calculation schema:
  // required for age >= 1 year, or age >= 3 years when using IOM formula.
  const ageYears = calculateAgeFromDateString(data.dateOfBirth);
  if (ageYears !== null) {
    const normalizedFormulaId = String(data.formulaId || '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
    const isIomFormula =
      normalizedFormulaId === 'iom' ||
      /(^|_)iom($|_)/.test(normalizedFormulaId) ||
      normalizedFormulaId.includes('institute_of_medicine');

    const requiresActivityLevel = ageYears >= 1 && (!isIomFormula || ageYears >= 3);
    if (requiresActivityLevel && (!data.activityLevel || data.activityLevel.trim() === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "This field is required",
        path: ["activityLevel"],
      });
    }
  }
});

// Initial state constants
export const INITIAL_FORM_DATA: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  phone_country_code: '+91',
  dateOfBirth: '',
  age: '',
  gender: '' as any,
  sexAtBirth: '' as any,
  location: '',
  cityOrCounty: '',
  zipCode: '',
  preferredMeasurementSystem: 'metric',
  weightKg: '',
  heightCm: '',
  activityLevel: '',
  feedingType: '',
  formulaId : '',
  drvFormulaId: '',
  waistCircumferenceCm: '',
  hipCircumferenceCm: '',
  bodyFatPercentage: '',
  pregnancyStatus: '',
  pregnancyTrimester: '',
  prePregnancyWeightKg: '',
  lactationStatus: 'none',
  twinPregnancy: '',
  weightLossGoal: '',
  customCalorieAdjustment: '',
  allergies: '',
  foodIntolerances: '',
  dietaryPreferences: '',
  cuisineTypes: '',
  medicalConditions: '',
  diabetesType: '',
  thyroidType: '',
  healthGoal: '',
};

export const INITIAL_EDITABLE_CALCULATED_DATA = {
  bmiScore: '',
  bmiClassification: '',
  bmr: '',
  eer: '',
};

// Form options
export const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
  { value: 'other', label: 'Other' },
];

export const SEX_AT_BIRTH_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

export const DIABETES_TYPE_OPTIONS = [
  { value: 'Type 1', label: 'Type 1' },
  { value: 'Type 2', label: 'Type 2' },
  { value: 'LADA', label: 'LADA' },
  { value: 'Gestational', label: 'Gestational' },
  { value: 'Prediabetes', label: 'Prediabetes' },
  { value: 'Not sure', label: 'Not sure' },
];

export const THYROID_TYPE_OPTIONS = [
  { value: 'Underactive (hypothyroid)', label: 'Underactive (hypothyroid)' },
  { value: 'Overactive (hyperthyroid)', label: 'Overactive (hyperthyroid)' },
  { value: "Hashimoto's", label: "Hashimoto's" },
  { value: 'Graves', label: 'Graves' },
  { value: 'Not sure', label: 'Not sure' },
];


export const PREGNANCY_TRIMESTER_OPTIONS = [
  { value: 'none', label: 'Not Pregnant' },
  { value: 'first', label: '1st Trimester' },
  { value: 'second', label: '2nd Trimester' },
  { value: 'third', label: '3rd Trimester' }
];

export const LACTATION_STATUS_OPTIONS = [
  { value: 'none', label: 'Not Lactating' },
  { value: '0-6', label: 'Lactating 0-6 Months' },
  { value: '6-12', label: 'Lactating 6-12 Months' },
  { value: '12-18', label: 'Lactating 12-18 Months' },
  { value: '18-24', label: 'Lactating 18-24 Months' }
];

export const PREGNANCY_TYPE_OPTIONS = [
  { value: 'singleton', label: 'Singleton' },
  { value: 'twin', label: 'Twin Pregnancy' }
];

export const WEIGHT_LOSS_GOAL_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'maintain', label: 'Maintain Weight' },
  { value: 'weight_loss', label: 'Lose Weight' }
];

export const GENERAL_WEIGHT_GOAL_OPTIONS = [
  { value: 'aggressive_loss', label: 'Aggressive Loss' },
  { value: 'moderate_loss', label: 'Moderate Loss' },
  { value: 'mild_loss', label: 'Mild Loss' },
  { value: 'maintain', label: 'Maintain Weight' },
  { value: 'mild_gain', label: 'Mild Gain' },
  { value: 'moderate_gain', label: 'Moderate Gain' },
  { value: 'aggressive_gain', label: 'Aggressive Gain' }
];

export const FEEDING_TYPE_OPTIONS = [
  { value: 'breastfed', label: 'Breast-fed' },
  { value: 'formula', label: 'Formula-fed' }
];

export const MEASUREMENT_SYSTEM_OPTIONS = [
  { value: 'metric', label: 'Metric (kg, cm, °C)' },
  { value: 'imperial', label: 'Imperial (lbs, ft/in, °F)' }
];
